import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || '';
const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '';

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    flowType: 'pkce',
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  }
});

// Database Types
export interface User {
  id: string;
  steam_id?: string;
  gamertag: string;
  referred_by?: string | null;
  referred_by_user_id?: string | null;
  platform?: ('PC' | 'PS5' | 'Xbox' | 'Nintendo Switch')[];
  bio?: string;
  profile_image_url?: string;
  game_images?: string[];
  steam_profile_url?: string;
  steam_avatar_url?: string;
  age?: number;
  gender?: string;
  country_code?: string | null;
  preferred_language?: string | null;
  discord_profile?: string;
  streaming_profile?: string;
  /** Bare handles, no URL and no leading @. Links are derived at render time. */
  twitch_handle?: string | null;
  youtube_handle?: string | null;
  x_handle?: string | null;
  tiktok_handle?: string | null;
  instagram_handle?: string | null;
  games?: string[];
  game_preferences?: {
    gameMode?: string;
    rankType?: string;
    playStyle?: string;
    strategy?: string;
    teamPlay?: string;
    leadership?: string;
    focusType?: string;
    characterPreference?: string;
    gamingSchedule?: string;
    micUsage?: string;
    personality?: string;
    conversationStyle?: string;
    inGameTools?: string;
  };
  world_id?: WorldIdVerification | null;
  // Weekly "when I usually play" slots like 'mon_evening' (lib/play-schedule.ts),
  // in the owner's IANA zone play_timezone. Optional: absent from rows that
  // don't select them (anon has no column grant for them yet).
  play_schedule?: string[] | null;
  play_timezone?: string | null;
  created_at: string;
}

export type WorldIdCredential = 'orb' | 'selfie';

export interface WorldIdVerification {
  user_id: string;
  verified_at: string;
  /**
   * Verification tier. 'orb' is the strong one-human-one-account signal;
   * 'selfie' (World ID Selfie Check) only proves liveness and expires.
   * Optional because rows selected by older queries won't include it.
   */
  credential?: WorldIdCredential | null;
  /** Null for orb (never expires); an ISO timestamp for selfie. */
  expires_at?: string | null;
}

/**
 * The single place the badge rule lives: a verification counts as active
 * unless it is a selfie credential whose 90-day window has passed.
 *
 * `credential` defaults to 'orb' when absent — every row written before
 * Selfie Check shipped was an Orb verification, and the column default
 * matches, so a query that omits the column can't accidentally expire one.
 */
export function isWorldIdActive(
  verification: WorldIdVerification | null | undefined
): boolean {
  if (!verification) return false;
  if ((verification.credential ?? 'orb') === 'orb') return true;
  if (!verification.expires_at) return false;
  return new Date(verification.expires_at).getTime() > Date.now();
}

export interface Clip {
  id: string;
  user_id: string;
  title?: string;
  description?: string;
  video_url: string;
  thumbnail_url?: string;
  duration?: number;
  width?: number;
  height?: number;
  file_size?: number;
  file_type?: string;
  is_public: boolean;
  display_order?: number | null;
  created_at: string;
  updated_at: string;
}

export interface UserGame {
  id: string;
  user_id: string;
  game_id: string;
  player_id?: string;
  platform?: 'PC' | 'PS5' | 'Xbox' | 'Nintendo Switch';
  created_at: string;
  updated_at: string;
}

export interface ApexProfile {
  user_game_id: string;
  uid?: string | null;
  name?: string | null;
  platform?: string | null;
  level?: number | null;
  level_prestige?: number | null;
  to_next_level_percent?: number | null;
  account_level?: number | null;
  is_banned?: boolean | null;
  br_rank_score?: number | null;
  br_rank_name?: string | null;
  br_rank_div?: number | null;
  br_rank_img?: string | null;
  selected_legend?: string | null;
  selected_legend_icon?: string | null;
  last_synced_at?: string | null;
  last_sync_status?: string | null;
  permanently_not_found?: boolean | null;
}

export interface FortniteProfile {
  user_game_id: string;
  account_id?: string | null;
  account_name?: string | null;
  account_type?: string | null;
  battle_pass_level?: number | null;
  battle_pass_progress?: number | null;
  input_source?: string | null;
  score?: number | null;
  wins?: number | null;
  top3?: number | null;
  top5?: number | null;
  top10?: number | null;
  top25?: number | null;
  kills?: number | null;
  deaths?: number | null;
  kd?: number | null;
  matches?: number | null;
  win_rate?: number | null;
  minutes_played?: number | null;
  players_outlived?: number | null;
  last_synced_at?: string | null;
  last_sync_status?: string | null;
  /** Upstream 403: the account hides its public match history. */
  stats_private?: boolean | null;
}

export interface Game {
  id: string;
  name: string;
  display_name: string;
  icon_name?: string;
  color?: string;
  screenshots?: string[];
  sort_order?: number;
  created_at: string;
  updated_at: string;
}

export interface Referral {
  id: string;
  referrer: string;
  referrer_user_id?: string | null;
  email: string;
  converted: boolean;
  created_at: string;
}

export interface PlayNowMessage {
  id: string;
  game_id: string;
  user_id: string;
  gamertag: string;
  profile_image_url?: string;
  message_text: string;
  created_at: string;
}

export interface GroupChat {
  id: string;
  title: string | null;
  kind: 'group' | 'support' | 'squad';
  is_system: boolean;
  created_at: string;
  last_message_at: string | null;
  tournament_id: string | null;
}

export interface GroupChatMember {
  group_chat_id: string;
  user_id: string;
  joined_at: string;
  last_read_at: string | null;
}

// 'user' = a normal message; 'join'/'leave' = system events rendered as a
// centered notice ("Alex joined the group"). 'join' is reused for "added".
export type GroupChatMessageType = 'user' | 'join' | 'leave';

export interface GroupChatMessage {
  id: string;
  group_chat_id: string;
  sender_id: string;
  content: string;
  created_at: string;
  pinned_at: string | null;
  pinned_by: string | null;
  /** Set when the sender soft-deleted the message; render a tombstone. */
  deleted_at?: string | null;
  message_type?: GroupChatMessageType;
  // user_ids @-mentioned in this message (null/absent for older rows)
  mentions?: string[] | null;
}

export interface GroupChatMessageReaction {
  id: string;
  message_id: string;
  user_id: string;
  emoji: string;
  created_at: string;
}

export interface GroupChatMemberProfile {
  user_id: string;
  gamertag: string;
  profile_image_url?: string;
  // When this member last opened the chat — powers iMessage-style read receipts.
  last_read_at?: string | null;
}

export interface GroupChatWithPreview extends GroupChat {
  members: GroupChatMemberProfile[];
  lastMessage?: Pick<GroupChatMessage, 'content' | 'created_at' | 'sender_id'>;
  hasUnread: boolean;
}

// Database table names
export const TABLES = {
  USERS: 'users',
  CLIPS: 'clips',
  GAMES: 'games',
  USER_GAMES: 'user_games',
  REFERRALS: 'referrals',
  PLAY_NOW_MESSAGES: 'play_now_messages',
  SWIPES: 'swipes',
  MATCHES: 'matches',
  GROUP_CHATS: 'group_chats',
  GROUP_CHAT_MEMBERS: 'group_chat_members',
  GROUP_CHAT_MESSAGES: 'group_chat_messages',
  GROUP_CHAT_MESSAGE_REACTIONS: 'group_chat_message_reactions',
  GAMERPLUG_TEAM_MEMBERS: 'gamerplug_team_members',
  WORLD_ID: 'world_id',
} as const;
